import dayjs from "dayjs";

import { formatEventTimezoneAbbrev } from "./eventDateTime";

const API_DATE = "YYYY-MM-DD";
export const AVAILABILITY_DATE_INPUT_FORMAT = "D MMM YYYY";

/** Short date without year, e.g. "12 Jan". */
export function formatAvailabilityDateShort(value: string | undefined | null): string {
  if (!value?.trim()) {
    return "";
  }
  const parsed = dayjs(value.trim(), API_DATE, true);
  return parsed.isValid() ? parsed.format("D MMM") : value;
}

/** Display range like "1 Jan – 31 Dec 2024" when same calendar year. */
export function formatAvailabilityDateRange(
  fromValue: string | undefined | null,
  toValue: string | undefined | null,
): string {
  const from = dayjs(fromValue?.trim(), API_DATE, true);
  const to = dayjs(toValue?.trim(), API_DATE, true);
  if (!from.isValid() || !to.isValid()) {
    return [fromValue, toValue].filter(Boolean).join(" – ");
  }
  if (from.year() === to.year()) {
    return `${from.format("D MMM")} – ${to.format("D MMM YYYY")}`;
  }
  return `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;
}

export function formatAvailabilityDateForApi(value: unknown): string {
  if (dayjs.isDayjs(value)) {
    return value.format(API_DATE);
  }
  if (typeof value === "string" && value.trim()) {
    const iso = dayjs(value.trim(), API_DATE, true);
    if (iso.isValid()) {
      return iso.format(API_DATE);
    }
    const display = dayjs(value.trim(), AVAILABILITY_DATE_INPUT_FORMAT, true);
    if (display.isValid()) {
      return display.format(API_DATE);
    }
  }
  return String(value ?? "");
}

/** e.g. "America/New_York" → "New York (EDT)" */
export function formatTimezoneLabel(timezone: string | undefined | null): string {
  if (!timezone?.trim()) {
    return "";
  }
  const trimmed = timezone.trim();
  const city = trimmed.split("/").pop()?.replace(/_/g, " ") ?? trimmed;
  const abbrev = formatEventTimezoneAbbrev(trimmed);
  if (!abbrev || abbrev === trimmed.replace(/_/g, " ")) {
    return city;
  }
  return `${city} (${abbrev})`;
}

export function formatAvailabilityHourLabel(hour: number): string {
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12} ${period}`;
}

export function buildAvailabilityHourOptions(): { value: number; label: string }[] {
  return Array.from({ length: 24 }, (_, hour) => ({
    value: hour,
    label: formatAvailabilityHourLabel(hour),
  }));
}

export function hourFromTimeField(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (dayjs.isDayjs(value)) {
    return value.hour();
  }
  if (typeof value !== "string") {
    return 0;
  }
  const trimmed = value.trim();
  if (trimmed.endsWith("PM") && !trimmed.startsWith("12")) {
    return parseInt(trimmed.split(":")[0], 10) + 12;
  }
  if (trimmed.endsWith("AM") && trimmed.startsWith("12")) {
    return 0;
  }
  return parseInt(trimmed.split(":")[0], 10);
}
